// assets/fit-predictor.js
//
// Drives the "Fit Predictor" block on the product page. Height AND weight are
// always used, in one of two modes chosen automatically per product:
//
// 1. Chart mode (data-chart is present): the product's custom.fit_predictor_chart
//    metafield points to a "Fit Chart" metaobject with a simple line-based band
//    list, e.g.:
//      150-162 XXS
//      163-166 XS
//      ...
//      194+ XXL
//    Height picks a BASE size from the matching band. Weight then nudges that
//    base size up or down within the chart's own size list (see
//    weightAdjustment()) -- so two people of the same height but different
//    builds can land on different sizes. New fit types (new metaobject
//    entries) work automatically, no code change, and the band list itself
//    never needs to mention weight. The size labels are free text, so a chart
//    may just as well list waist sizes (29, 30, 31 ...) as letter sizes.
//    A style that runs small or large gets its own chart with the labels
//    shifted a rung, rather than a code change.
//
//    A chart's "Basis" field flips which measurement the bands are read
//    against. Left empty (or "lengte") the bands are heights in cm, as above.
//    Set to "gewicht" the bands are weights in kg and height becomes the
//    nudge instead -- the right way round for jeans and other bottoms, where
//    the waist tracks weight far more closely than height. Example:
//      30-59 30
//      60-66 31
//      ...
//      90+ 36
//
// 2. Formula mode (no chart set): falls back to a STANDARD, generic formula.
//    Which formula is used depends on the product's custom.fit_predictor_range:
//      - Letter ranges (XXS-XXL / XS-XXL / S-XL) use BMI (weight / height^2)
//        with a small height correction, clamped to that range.
//      - The waist range (29-36), used for jeans and other bottoms sold on
//        waist size, is driven by WEIGHT first with a height correction --
//        a waist measurement tracks weight far more closely than height.
//    To tune: adjust BMI_BREAKPOINTS / heightAdjustment() for letter sizes,
//    WAIST_WEIGHT_BREAKPOINTS / waistHeightAdjustment() for waist sizes, or
//    add a Fit Chart for that product instead.
import { Component } from '@theme/component';

/**
 * @typedef {object} FitPredictorRefs
 * @property {HTMLInputElement} heightInput
 * @property {HTMLInputElement} weightInput
 * @property {HTMLElement} result
 * @property {HTMLElement} resultSize
 * @property {HTMLElement} errorMessage
 */

const SIZE_LADDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL'];

// Jeans and other bottoms are sold on waist size. Mansour does not produce a
// 35, so the ladder steps straight from 34 to 36.
const WAIST_LADDER = ['29', '30', '31', '32', '33', '34', '36'];

// The custom.fit_predictor_range value that selects the waist formula.
const WAIST_RANGE_KEY = '29-36';

// Values of a Fit Chart's "Basis" field: what its bands are measured against.
const CHART_BASIS_HEIGHT = 'lengte';
const CHART_BASIS_WEIGHT = 'gewicht';

// BMI value at/above which the prediction moves to the next size up.
// (Formula mode, letter sizes only -- see predictSize().)
const BMI_BREAKPOINTS = [19, 21, 23, 25, 27.5, 30];

// Weight in kg at/above which the predicted WAIST moves to the next size up,
// one breakpoint per step in WAIST_LADDER. (Formula mode, waist sizes only.)
const WAIST_WEIGHT_BREAKPOINTS = [60, 67, 74, 82, 90, 100];

// Maps the custom.fit_predictor_range metafield value to [minIndex, maxIndex]
// bounds within SIZE_LADDER (formula mode, letter sizes only).
const RANGE_TO_LADDER_BOUNDS = {
  'XXS-XXL': [0, 6],
  'XS-XXL': [1, 6],
  'S-XL': [2, 5],
};

const MIN_HEIGHT_CM = 120;
const MAX_HEIGHT_CM = 230;
const MIN_WEIGHT_KG = 30;
const MAX_WEIGHT_KG = 200;

// Matches lines like "150-162 XXS" or "194+ XXL". Lines that don't match
// (blank lines, "# comment" placeholders) are skipped.
const BAND_LINE_PATTERN = /^(\d+)\s*(?:-\s*(\d+))?\+?\s+(\S+)$/;

/**
 * @param {string} bandsText
 * @returns {{ min: number, max: number, size: string }[]}
 */
export function parseBands(bandsText) {
  if (!bandsText) return [];

  return bandsText
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line && !line.startsWith('#'))
    .map((line) => {
      const match = line.match(BAND_LINE_PATTERN);
      if (!match) return null;

      const min = Number(match[1]);
      const max = match[2] !== undefined ? Number(match[2]) : Infinity;
      const size = match[3];

      return { min, max, size };
    })
    .filter((band) => band !== null)
    .sort((a, b) => a.min - b.min);
}

/**
 * @param {{ min: number, max: number, size: string }[]} bands
 * @param {number} value height in cm, or weight in kg for a weight-based chart
 * @returns {string | null}
 */
export function sizeFromBands(bands, value) {
  if (!bands.length) return null;

  const match = bands.find((band) => value >= band.min && value <= band.max);
  if (match) return match.size;

  // Outside every band: clamp to the nearest end rather than showing nothing.
  return value < bands[0].min ? bands[0].size : bands[bands.length - 1].size;
}

/**
 * The chart's own size ladder, in the order sizes first appear across the
 * (already sorted) bands. Duplicate labels (e.g. a fit that caps out at "XL"
 * for several tall-height bands) only appear once.
 * @param {{ min: number, max: number, size: string }[]} bands
 * @returns {string[]}
 */
export function buildLadder(bands) {
  const seen = new Set();
  const ladder = [];
  for (const band of bands) {
    if (!seen.has(band.size)) {
      seen.add(band.size);
      ladder.push(band.size);
    }
  }
  return ladder;
}

/**
 * @param {number} bmi
 * @returns {number} index into a 7-point BMI scale (0-6)
 */
function bmiToIndex(bmi) {
  let index = 0;
  for (const breakpoint of BMI_BREAKPOINTS) {
    if (bmi < breakpoint) break;
    index += 1;
  }
  return index;
}

/**
 * How many rungs to shift on a chart's size ladder based on weight. On a
 * height-based chart height stays the primary signal (it picks the base
 * size); weight only nudges that base size, and only once someone is clearly
 * leaner or heavier than an average build for their height -- a normal BMI
 * (roughly 20-25) never shifts the size at all.
 * @param {number} heightCm
 * @param {number} weightKg
 */
function weightAdjustment(heightCm, weightKg) {
  const heightM = heightCm / 100;
  const bmi = weightKg / (heightM * heightM);

  if (bmi < 17) return -2;
  if (bmi < 20) return -1;
  if (bmi < 25) return 0;
  if (bmi < 29) return 1;
  return 2;
}

/**
 * Chart mode. With the default "lengte" basis, height picks the base size
 * from the bands and weight nudges it within that chart's own size list.
 * With the "gewicht" basis the two swap round: weight picks the base size
 * and height nudges it -- used for bottoms sold on waist size.
 * @param {{ min: number, max: number, size: string }[]} bands
 * @param {number} heightCm
 * @param {number} weightKg
 * @param {string} [basis] 'lengte' (default) or 'gewicht'
 * @returns {string | null}
 */
export function sizeFromChart(bands, heightCm, weightKg, basis = CHART_BASIS_HEIGHT) {
  if (!bands.length) return null;

  const byWeight = basis === CHART_BASIS_WEIGHT;
  const ladder = buildLadder(bands);
  const baseSize = sizeFromBands(bands, byWeight ? weightKg : heightCm);
  const baseIndex = ladder.indexOf(baseSize);

  if (baseIndex === -1) return baseSize;

  const shift = byWeight ? waistHeightAdjustment(heightCm) : weightAdjustment(heightCm, weightKg);
  const index = Math.min(Math.max(baseIndex + shift, 0), ladder.length - 1);
  return ladder[index];
}

/**
 * Taller-than-average frames tend to need a size up for sleeve/inseam length;
 * shorter frames a size down, independent of BMI. Letter sizes.
 * @param {number} heightCm
 */
function heightAdjustment(heightCm) {
  if (heightCm >= 190) return 1;
  if (heightCm < 165) return -1;
  return 0;
}

/**
 * The other way round from heightAdjustment(): for a WAIST measurement the
 * same weight spread over a taller frame means a slimmer middle, while a
 * shorter frame carries it around the waist. Waist sizes.
 * @param {number} heightCm
 */
function waistHeightAdjustment(heightCm) {
  if (heightCm >= 190) return -1;
  if (heightCm < 168) return 1;
  return 0;
}

/**
 * Formula mode for bottoms sold on waist size (range "29-36"). Weight picks
 * the base waist, height corrects it by at most one step.
 * @param {number} heightCm
 * @param {number} weightKg
 * @returns {string}
 */
export function predictWaistSize(heightCm, weightKg) {
  let index = 0;
  for (const breakpoint of WAIST_WEIGHT_BREAKPOINTS) {
    if (weightKg < breakpoint) break;
    index += 1;
  }

  index = Math.min(Math.max(index + waistHeightAdjustment(heightCm), 0), WAIST_LADDER.length - 1);

  const size = WAIST_LADDER[index];
  if (!size) throw new Error(`Fit Predictor: no waist size resolved for index ${index}`);
  return size;
}

/**
 * Formula mode (no chart assigned to the product).
 * @param {number} heightCm
 * @param {number} weightKg
 * @param {string} rangeKey
 * @returns {string}
 */
export function predictSize(heightCm, weightKg, rangeKey) {
  if (rangeKey === WAIST_RANGE_KEY) return predictWaistSize(heightCm, weightKg);

  const bmi = weightKg / ((heightCm / 100) * (heightCm / 100));

  let index = bmiToIndex(bmi) + heightAdjustment(heightCm);

  const [min, max] = RANGE_TO_LADDER_BOUNDS[rangeKey] || RANGE_TO_LADDER_BOUNDS['S-XL'];
  index = Math.min(Math.max(index, min), max);

  const size = SIZE_LADDER[index];
  if (!size) throw new Error(`Fit Predictor: no size resolved for index ${index}`);
  return size;
}

/**
 * @extends Component<FitPredictorRefs>
 */
class FitPredictor extends Component {
  calculate() {
    const { heightInput, weightInput, result, resultSize, errorMessage } = this.refs;

    const height = Number(heightInput.value);
    const weight = Number(weightInput.value);

    errorMessage.hidden = true;
    result.hidden = true;

    const heightValid = Number.isFinite(height) && height >= MIN_HEIGHT_CM && height <= MAX_HEIGHT_CM;
    const weightValid = Number.isFinite(weight) && weight >= MIN_WEIGHT_KG && weight <= MAX_WEIGHT_KG;

    if (!heightValid || !weightValid) {
      errorMessage.hidden = false;
      return;
    }

    const chartText = this.dataset.chart || '';
    const bands = parseBands(chartText);

    if (bands.length > 0) {
      const basis = this.dataset.basis || CHART_BASIS_HEIGHT;
      const size = sizeFromChart(bands, height, weight, basis);
      resultSize.textContent = size ?? '';
      result.hidden = false;
      return;
    }

    if (chartText.trim()) {
      // A chart was assigned to this product but has no usable rows yet
      // (e.g. a placeholder chart waiting on real measurements).
      errorMessage.hidden = false;
      return;
    }

    const rangeKey = this.dataset.range || 'S-XL';
    const size = predictSize(height, weight, rangeKey);

    resultSize.textContent = size;
    result.hidden = false;
  }
}

if (!customElements.get('fit-predictor-component')) {
  customElements.define('fit-predictor-component', FitPredictor);
}
